<script>
    /**
     * SPDX-FileCopyrightText: 2012-2026 Open Assessment Technologies S.A.
     * Copyright (C) 2026 (original work) Open Assessment Technologies S.A.
     *
     * SPDX-License-Identifier: AGPL-3.0-only OR LicenseRef-TAO-Commercial-License
     */

    import { getContext, onDestroy } from 'svelte';
    // Components
    import LoggingPlayer from '../interactions/media/LoggingPlayer.svelte';
    // Utils
    import { htmlAttributes } from './util/attributes.js';
    import { withUnit } from '../util/size.js';
    import { throttle } from 'lodash';
    // Store
    import { getItemStateStore } from '../itemsStateStore.js';
    import { getItemSessionStatusStore } from '../itemsSessionStatusStore.js';
    import { getItemSettingsStore } from '../itemsSettingsStore.js';
    import { getLanguageDirection } from '@oat-sa-private/ui-core';
    // Constants
    import itemSessionStatus from '../itemSessionStatus';

    let { itemIdentifier, attributes = {} } = $props();

    // svelte-ignore state_referenced_locally
    const itemContext = getContext(itemIdentifier);
    const instructionsLang = itemContext && itemContext.getInstructionsLang();
    const instructionsDir = instructionsLang ? getLanguageDirection(instructionsLang) : void 0;
    const assetManager = itemContext && itemContext.getAssetManager();

    // svelte-ignore state_referenced_locally
    const itemStateStore = getItemStateStore(itemIdentifier),
        itemSessionStatusStore = getItemSessionStatusStore(itemIdentifier),
        itemSettingsStore = getItemSettingsStore(itemIdentifier);

    const throttledStoreProgress = throttle(storeProgress, 400);

    // svelte-ignore non_reactive_update
    let src, type, width, classes, staticElementId;

    setDerivedProps();

    function setDerivedProps() {
        src = assetManager ? assetManager.resolve(attributes.src) : attributes.src;
        type = attributes.type || 'video/*';
        width = withUnit(attributes.width) || '100%';
        classes = attributes.class || '';
        staticElementId = `static_video_${attributes.serial}`;
    }

    let startTime = $state(0);
    let currentTime = 0;
    let isDestroyed = false;
    let interactionElement;
    const getInteractionElement = () => interactionElement;

    loadProgress();

    let disabledBySession = $derived(
        $itemSessionStatusStore === itemSessionStatus.suspended ||
            $itemSessionStatusStore === itemSessionStatus.closed ||
            !!$itemSettingsStore.doNotPlayMedia
    );

    /**
     * Restore progress from store
     */
    function loadProgress() {
        if (attributes.serial) {
            const elementState = itemStateStore.getItemElementState(staticElementId);
            if (elementState && elementState.time) {
                startTime = elementState.time;
            }
        }
    }

    /**
     * Save progress in store
     */
    function storeProgress() {
        if (!isDestroyed && attributes.serial) {
            itemStateStore.setItemElementState(staticElementId, { time: currentTime });
        }
    }

    /**
     * Handle player time update
     * @param {Number} time
     */
    function handleTimeUpdate(time) {
        currentTime = time;
        throttledStoreProgress();
    }

    onDestroy(() => {
        isDestroyed = true;
    });
</script>

<div
    bind:this={interactionElement}
    class="qti-video-container not-printable {classes}"
    {...htmlAttributes(attributes, ['class'])}>
    <LoggingPlayer
        {src}
        {type}
        {width}
        {startTime}
        disabled={disabledBySession}
        feedbackLang={instructionsLang}
        dir={instructionsDir}
        fullScreen={true}
        ontimeupdate={handleTimeUpdate}
        {getInteractionElement}
        {staticElementId} />
</div>
